import {
    DataTable,
    DataTableCell,
    DataTablePresenter,
    getAdjustmentCode,
    getRemarkCode,
    ITableColumn,
    renderAmount,
    renderEmpty,
    renderString,
    renderStringGroup,
    ProviderClaimPaymentItem,
    ProviderClaimPaymentItemAdjustment,
} from '@enter/core';
import React, { useMemo } from 'react';
import { CellProps } from 'react-table';

interface IPaymentItemsProps {
    items: ProviderClaimPaymentItem[];
}

export const PaymentItems: React.FunctionComponent<IPaymentItemsProps> = React.memo(({ items }) => {
    const columns: ITableColumn<ProviderClaimPaymentItem>[] = useMemo<ITableColumn<ProviderClaimPaymentItem>[]>(
        () => [
            {
                id: 'ControlNumber',
                Header: 'Control Number',
                accessor: 'controlNumber',
                Cell: DataTableCell('String'),
                disableSortBy: true,
            },
            {
                id: 'ProcedureCode',
                Header: 'Procedure Code',
                accessor: 'procedureCode',
                Cell: DataTableCell('String'),
                disableSortBy: true,
            },
            {
                id: 'ModifierCodes',
                Header: 'Modifier Codes',
                accessor: 'modifierCodes',
                Cell: DataTableCell('StringGroup'),
                disableSortBy: true,
            },
            {
                id: 'RemarkCodes',
                Header: 'Remark Codes',
                accessor: 'remarkCodes',
                Cell: (props: CellProps<ProviderClaimPaymentItem>) => {
                    const remarkCodes = props.row.original.remarkCodes;
                    if (!remarkCodes?.length) {
                        return renderEmpty();
                    }

                    return DataTableCell('AsyncList', {
                        type: 'AsyncList',
                        loadMethod: async (): Promise<React.ReactElement> => {
                            const mapped = remarkCodes.map(code => getRemarkCode(code));
                            return <>{renderStringGroup(mapped)}</>;
                        },
                    })(props);
                },
                disableSortBy: true,
            },
            {
                id: 'ChargeAmount',
                Header: 'Charge amount',
                accessor: 'chargeAmount',
                Cell: DataTableCell('Amount'),
                disableSortBy: true,
            },
            {
                id: 'ChargeCount',
                Header: 'Unit',
                accessor: 'chargeCount',
                Cell: DataTableCell('String'),
                disableSortBy: true,
            },
            {
                id: 'AllowedAmount',
                Header: 'Allowed amount',
                accessor: 'allowedAmount',
                Cell: DataTableCell('Amount'),
                disableSortBy: true,
            },
            {
                id: 'PaidAmount',
                Header: 'Paid amount',
                accessor: 'paidAmount',
                Cell: DataTableCell('Amount'),
                disableSortBy: true,
            },
            {
                id: 'FromDOS',
                Header: 'From DOS',
                accessor: 'fromDos',
                Cell: DataTableCell('Date'),
                disableSortBy: true,
                width: 150,
            },
            {
                id: 'ThroughDOS',
                Header: 'Through DOS',
                accessor: 'throughDos',
                Cell: DataTableCell('Date'),
                disableSortBy: true,
                show: false,
                width: 150,
            },
            {
                id: 'PlaceOfServiceType',
                Header: 'Place of service code',
                accessor: 'placeOfServiceCode',
                Cell: DataTableCell('String'),
                disableSortBy: true,
                show: false,
            },
            {
                id: 'Adjustments',
                Header: 'Adjustments',
                accessor: (): string => 'Details',
                width: 200,
                Cell: (props: CellProps<ProviderClaimPaymentItem>) => {
                    const adjustments = props.row.original.adjustments;
                    if (!adjustments?.length) {
                        return renderEmpty();
                    }

                    return DataTableCell('AsyncList', {
                        type: 'AsyncList',
                        loadMethod: async (): Promise<React.ReactElement> => (
                            <div>
                                {adjustments.map((adjustment, index) => (
                                    <div key={index}>
                                        {adjustment.group && <span>{renderString(adjustment.group)}: </span>}
                                        {adjustment.reasonCode && (
                                            <span>{renderString(getAdjustmentCode(adjustment.reasonCode))} </span>
                                        )}
                                        <span>{renderAmount(adjustment.amount)}</span>
                                        {typeof adjustment.units !== 'undefined' && (
                                            <span> ({renderString(String(adjustment.units))})</span>
                                        )}
                                    </div>
                                ))}
                            </div>
                        ),
                    })(props);
                },
                disableSortBy: true,
            },
        ],
        [],
    );

    const { current: presenter } = React.useRef(
        new DataTablePresenter({
            name: 'payment-items',
            data: items,
            columns,
            filterSchemas: [],
        }),
    );

    presenter.onDataUpdate(items);

    return <DataTable presenter={presenter} hidePagination={true} hideFiltering={true} isLoading={false} />;
});
